/**
 * Client-side authenticated fetch wrapper for the SPA architecture.
 *
 * - Stores the access token in-memory only (never localStorage) for XSS protection.
 * - Attaches `Authorization: Bearer <token>` on every request.
 * - On 401, transparently calls POST /auth/refresh (cookie sent automatically).
 * - Queues concurrent requests during a token refresh to avoid race conditions.
 */

const BASE_URL = (import.meta.env.VITE_BASE_BACKEND_URL || '/api') as string

// ---------------------------------------------------------------------------
// In-memory access token store
// ---------------------------------------------------------------------------

let accessToken: string | null = null

export function getAccessToken(): string | null {
  return accessToken
}

function clearLegacyAccessTokenCookies(): void {
  if (typeof document === 'undefined') return

  const expires = '; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
  document.cookie = `aim-auth=${expires}`

  // Remove Domain= cookies created before access tokens became memory-only.
  // Do not derive a parent domain for arbitrary hosts.
  const host = window.location.hostname
  if (host === 'reliefhub.app' || host === 'www.reliefhub.app') {
    document.cookie = `aim-auth=${expires}; domain=.reliefhub.app`
  } else if (host.endsWith('.reliefhub.app')) {
    document.cookie = `aim-auth=${expires}; domain=.${host}`
  }
}

export function setAccessToken(token: string | null): void {
  accessToken = token
  // Access tokens intentionally remain in memory. Clear the obsolete
  // JavaScript-readable cookie so it cannot create a second credential set.
  clearLegacyAccessTokenCookies()
}

export function clearAccessToken(): void {
  setAccessToken(null)
}

export function hasValidAccessToken(): boolean {
  if (!accessToken) return false

  try {
    const payload = JSON.parse(
      atob(accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
    ) as { exp?: number }
    // Refresh slightly early, rather than allowing an in-flight API request
    // to use a token that will expire moments later.
    return typeof payload.exp === 'number' && payload.exp * 1000 > Date.now() + 30_000
  } catch {
    return false
  }
}

export function getUserIdFromToken(): number {
  const token = getAccessToken()
  if (!token) throw new Error("No session found")
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")))
    return parseInt(payload.sub)
  } catch {
    throw new Error("No session found")
  }
}

// ---------------------------------------------------------------------------
// Token refresh queue — prevents parallel refresh calls
// ---------------------------------------------------------------------------

let isRefreshing = false
let refreshPromise: Promise<string | null> | null = null

/**
 * Call POST /auth/refresh. The httpOnly refresh cookie is sent automatically
 * because the request goes to the same origin (or CORS with credentials).
 * Returns the new access token, or null if the refresh failed.
 */
async function refreshAccessToken(): Promise<string | null> {
  try {
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include', // send refresh cookie
      headers: { 'Content-Type': 'application/json' },
    })

    if (!res.ok) return null

    const data = await res.json()
    const newToken: string | undefined = data?.data?.accessToken
    if (!newToken) return null

    setAccessToken(newToken)
    return newToken
  } catch {
    return null
  }
}

async function refreshAcrossTabs(): Promise<string | null> {
  // Refresh-token rotation is single-use. Web Locks serializes refreshes from
  // separate tabs, allowing the later tab to send the cookie rotated by the
  // earlier one instead of replaying the old token and revoking its family.
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return navigator.locks.request('reliefhub-auth-refresh', () => refreshAccessToken())
  }

  return refreshAccessToken()
}

/**
 * Ensures only one refresh is in-flight in this tab and coordinates refreshes
 * across tabs when the browser supports the Web Locks API.
 */
export async function refreshOnce(): Promise<string | null> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  isRefreshing = true
  refreshPromise = refreshAcrossTabs().finally(() => {
    isRefreshing = false
    refreshPromise = null
  })

  return refreshPromise
}

// ---------------------------------------------------------------------------
// authFetch — drop-in replacement for fetch with auth + auto-retry
// ---------------------------------------------------------------------------

export interface AuthFetchOptions extends RequestInit {
  /** If true, skip attaching the Authorization header (e.g., for login/signup). */
  skipAuth?: boolean
}

/**
 * Authenticated fetch wrapper.
 *
 * Usage:
 * ```ts
 * const res = await authFetch('/volunteers', { method: 'GET' })
 * const data = await res.json()
 * ```
 */
export async function authFetch(
  urlOrPath: string,
  options: AuthFetchOptions = {},
): Promise<Response> {
  const { skipAuth, ...fetchOptions } = options

  // Resolve full URL if a relative path was provided
  const url = urlOrPath.startsWith('http') || urlOrPath.startsWith(BASE_URL)
    ? urlOrPath
    : `${BASE_URL}${urlOrPath.startsWith('/') ? '' : '/'}${urlOrPath}`

  const headers: Record<string, string> = {
    ...(fetchOptions.headers as Record<string, string>),
  }

  // Attach Bearer token unless explicitly skipped
  if (!skipAuth && accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }

  // Default to JSON content type for non-FormData bodies
  if (fetchOptions.body && !(fetchOptions.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json'
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
    credentials: 'include', // always send cookies (for refresh endpoint)
  })

  // On 401, attempt a silent token refresh and retry once
  if (response.status === 401 && !skipAuth) {
    const newToken = await refreshOnce()
    if (newToken) {
      const retryHeaders: Record<string, string> = {
        ...(fetchOptions.headers as Record<string, string>),
        'Authorization': `Bearer ${newToken}`,
      }
      if (fetchOptions.body && !(fetchOptions.body instanceof FormData) && !retryHeaders['Content-Type']) {
        retryHeaders['Content-Type'] = 'application/json'
      }
      return fetch(url, {
        ...fetchOptions,
        headers: retryHeaders,
        credentials: 'include',
      })
    }
  }

  return response
}

// ---------------------------------------------------------------------------
// Helper: parse API response envelope
// ---------------------------------------------------------------------------

import { ApiResponse } from '@/shared/types/backend-response.type'

/**
 * Convenience wrapper that calls authFetch and parses the JSON response.
 * Returns a typed ApiResponse<T>.
 */
export async function authFetchJson<T>(
  urlOrPath: string,
  options: AuthFetchOptions = {},
): Promise<ApiResponse<T>> {
  try {
    const response = await authFetch(urlOrPath, options)

    if (!response.ok) {
      const errorText = await response.text()
      let errorMessage = errorText
      try {
        const errorJson = JSON.parse(errorText)
        errorMessage = errorJson.message || errorText
      } catch {
        // keep original text
      }
      if (!errorMessage || String(errorMessage).trim() === '') {
        errorMessage = `${response.status} ${response.statusText}`
      }
      return {
        success: false,
        message: errorMessage,
        status: response.status,
      } as ApiResponse<T>
    }

    const contentType = response.headers.get('content-type')
    if (!contentType || !contentType.includes('application/json')) {
      return {
        success: false,
        message: `Expected JSON but got: ${response.status} ${response.statusText}`,
        status: response.status,
      } as ApiResponse<T>
    }

    return (await response.json()) as ApiResponse<T>
  } catch (error) {
    const errMessage = error instanceof Error ? error.message : String(error)
    return {
      success: false,
      message: `Network error: ${errMessage}`,
    } as ApiResponse<T>
  }
}
